/**
 * Steps v2 client — `/api/board/steps/*`.
 *
 * Every call resolves to the server's steps payload:
 *   { ok, steps, currentStepId, currentStep, contextRecommendations, goalHistory, stepsAt, ...extra }
 * so the store can apply one shape everywhere (see useBoardStore.applyStepsPayload).
 */
import { SERVER_ROOT } from 'env';
import { PlanLimitError } from './boardAiApi';
import { withAiInputDebug } from './aiInputDebug';

const stepsRoot = `${SERVER_ROOT}/api/board/steps`;

function throwIfNotOk(res, data) {
  if (res.ok) return;
  const code = String(data?.code || '');
  if (res.status === 402 || code.includes('LIMIT') || code.endsWith('_REQUIRED') || data?.upgradeTo) {
    throw new PlanLimitError(data || { error: 'Plan limit reached' });
  }
  const err = new Error(data?.error || res.statusText || 'Steps request failed');
  err.status = res.status;
  err.body = data;
  throw err;
}

async function request(method, path, body) {
  let res;
  try {
    res = await fetch(`${stepsRoot}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Could not reach server. Check your connection and try again.');
  }
  const data = await res.json().catch(() => ({}));
  throwIfNotOk(res, data);
  return data;
}

/** Board-goal Run: full-canvas coach rewrites the open steps. */
export function generateBoardSteps(hashtag, clientContext) {
  return request('POST', '/generate', withAiInputDebug({ hashtag, clientContext }));
}

/** Refresh: whole new open list from the ledger + digest. */
export function patchBoardSteps(hashtag, clientContext) {
  return request('POST', '/patch', withAiInputDebug({ hashtag, clientContext }));
}

/** Chips for one pack-less step. */
export function fillBoardStep(hashtag, stepId, clientContext, { force = false } = {}) {
  return request('POST', '/fill', withAiInputDebug({ hashtag, stepId, clientContext, force }));
}

/** Point at an existing step by id. */
export function setBoardCurrentStep(hashtag, stepId) {
  return request('PATCH', '/current', { hashtag, stepId });
}

/** Status / progress / achievementRate / title of one step. */
export function updateBoardStep(hashtag, stepId, patch = {}) {
  return request('PATCH', `/${encodeURIComponent(stepId)}`, { hashtag, ...patch });
}

/** Permanently remove a step. */
export function deleteBoardStep(hashtag, stepId) {
  return request('DELETE', `/${encodeURIComponent(stepId)}`, { hashtag });
}

/** Ledger-only write (constraints and/or pendingDigest). */
export function patchBoardStepConstraints(hashtag, { constraints, pendingDigest } = {}) {
  return request('PATCH', '/constraints', {
    hashtag,
    ...(Array.isArray(constraints) ? { constraints } : {}),
    ...(pendingDigest && typeof pendingDigest === 'object' ? { pendingDigest } : {}),
  });
}
